import type { JSX } from "react";

import { useAuth } from "@/shared/auth";

/**
 * Renders a campaign/promotion creator as avatar initials + a readable label,
 * instead of leaking the raw 36-char `created_by` UUID.
 *
 * Resolution strategy:
 *   1. `created_by_name` from the API (BE-resolved display name, e.g.
 *      "Bootstrap Admin"). Works for ALL creators, not just the logged-in user.
 *   2. If that is empty (creator user unknown to the BE) and the row's
 *      `created_by` matches the logged-in user, fall back to their display name
 *      (or email) from the auth/session context.
 *   3. Otherwise a short form (first 8 chars of the id), NOT the full UUID.
 */

function initials(label: string): string {
  const cleaned = label.trim();
  if (!cleaned) return "?";
  // Email → use the part before "@".
  const local = cleaned.includes("@") ? cleaned.split("@")[0]! : cleaned;
  const words = local.split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

interface CreatedByProps {
  createdBy: string;
  /**
   * BE-resolved display name for `createdBy`. Empty string (or undefined) when
   * the creator user is unknown — then we fall back to the auth/short-id chain.
   */
  createdByName?: string | undefined;
}

export function CreatedBy({ createdBy, createdByName }: CreatedByProps): JSX.Element {
  const { user } = useAuth();

  const isCurrentUser = user != null && user.id === createdBy;
  const resolvedName = createdByName?.trim();

  // Whether we resolved a real human name (vs. a short id placeholder).
  const named = Boolean(resolvedName) || isCurrentUser;
  const label = resolvedName
    ? resolvedName
    : isCurrentUser
      ? user!.display_name || user!.email
      : // Foreign id with no resolved name: short form only (avoid leaking the full UUID).
        createdBy.slice(0, 8);

  return (
    <span
      className="inline-flex items-center gap-2 text-[13px] text-[var(--text-secondary)]"
      data-testid="created-by"
      data-current-user={isCurrentUser ? "true" : "false"}
      data-named={named ? "true" : "false"}
      title={named ? label : createdBy}
    >
      <span
        aria-hidden
        className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white"
        style={{ background: "var(--grad-primary-btn)" }}
      >
        {initials(label)}
      </span>
      <span className="truncate">{label}</span>
    </span>
  );
}
